import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  resumeService,
  ProcessJobsProgressEvent,
  ProcessJobWindow,
  AiTitleMatchProgressEvent,
  MatchJobsProgressEvent,
} from '../services/resumeService';
import { DetailedResume } from '../types/resume';
import { JobForYou } from '../types/jobs';
import {
  JobDatePreset,
  jobPassesDateFilter,
} from '../utils/jobDates';
import { calculateConfidenceScore } from '../utils/jobConfidence';
import { downloadJobsCsv } from '../utils/exportJobsCsv';

function formatLastProcessed(value?: string | null): string {
  if (!value) return 'Never processed';
  try {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return String(value);
    return dt.toLocaleString();
  } catch {
    return String(value);
  }
}

type CsvExportScope = 'all' | 'ai_title' | 'fit_and_network';

function jobHasDescriptionFit(job: JobForYou): boolean {
  return job.jobFit === 'high' || job.jobFit === 'medium' || job.jobFit === 'low';
}

function jobHasCompanyNetworkScore(job: JobForYou): boolean {
  if (typeof job.connectionScore === 'number') return true;
  return (
    job.connectionStrength != null &&
    String(job.connectionStrength).trim().length > 0
  );
}

function fitTieBreakRank(jobFit: JobForYou['jobFit']): number {
  if (jobFit === 'high') return 3;
  if (jobFit === 'medium') return 2;
  if (jobFit === 'low') return 1;
  return 0;
}

function normalizeForSearch(value: string): string {
  return value.toLowerCase().trim();
}

function jobMatchesStage3Search(job: JobForYou, query: string): boolean {
  const q = normalizeForSearch(query);
  if (!q) return true;
  const locations = Array.isArray(job.location)
    ? job.location.join(' ')
    : (job.location || '');
  const haystack = normalizeForSearch(
    `${job.postingTitle || ''} ${job.sourceCompany || ''} ${locations} ${job.platform || ''}`,
  );
  return haystack.includes(q);
}

function formatEtaSeconds(sec: number): string {
  if (sec <= 0) return 'a few seconds';
  if (sec < 90) return `~${sec} seconds`;
  if (sec < 3600) return `~${Math.max(1, Math.round(sec / 60))} minutes`;
  return `~${Math.round(sec / 3600)} hours`;
}

const JobsForYouPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const step1Ref = useRef<HTMLDivElement>(null);
  const step2Ref = useRef<HTMLDivElement>(null);
  const step3Ref = useRef<HTMLDivElement>(null);

  const [resumes, setResumes] = useState<DetailedResume[]>([]);
  const [selectedResumeId, setSelectedResumeId] = useState<string | null>(null);
  const [jobs, setJobs] = useState<JobForYou[]>([]);
  const [loadingResumes, setLoadingResumes] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [error, setError] = useState('');

  const [viewStep, setViewStep] = useState<'main' | 'ai'>('main');
  const [viewMode, setViewMode] = useState<'list' | 'company' | 'platform'>('list');
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');
  const [aiMatching, setAiMatching] = useState(false);
  const [aiTitleProgress, setAiTitleProgress] = useState<AiTitleMatchProgressEvent | null>(null);
  const [aiMessage, setAiMessage] = useState('');
  const [northAmericaOnly, setNorthAmericaOnly] = useState(true);

  const [datePreset, setDatePreset] = useState<JobDatePreset>('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  const [allCompanies, setAllCompanies] = useState(true);
  /** Phase 1 only: which job-post-urls rows to scan by Firestore `createdAt` (backend `jobWindow`). */
  const [processJobWindow, setProcessJobWindow] = useState<ProcessJobWindow>('all');
  const [processingJobs, setProcessingJobs] = useState(false);
  const [processScanProgress, setProcessScanProgress] = useState<ProcessJobsProgressEvent | null>(null);
  const [processMessage, setProcessMessage] = useState('');

  const [networkWeight, setNetworkWeight] = useState(70);
  const fitWeight = 100 - networkWeight;
  // Regular users default Google/Amazon jobs on; admins start with both off (can toggle)
  const [searchGoogleJobs, setSearchGoogleJobs] = useState(!isAdmin);
  const [searchAmazonJobs, setSearchAmazonJobs] = useState(!isAdmin);
  const [matchRecentDomainFocus, setMatchRecentDomainFocus] = useState(false);
  const [jobFitMatching, setJobFitMatching] = useState(false);
  const [matchFitProgress, setMatchFitProgress] = useState<MatchJobsProgressEvent | null>(null);
  const [matchMessage, setMatchMessage] = useState('');
  const [csvExportScope, setCsvExportScope] = useState<CsvExportScope>('all');
  const [csvExportScopeUserSet, setCsvExportScopeUserSet] = useState(false);
  const [stage3SearchQuery, setStage3SearchQuery] = useState('');
  const [advancedOptionsOpen, setAdvancedOptionsOpen] = useState(false);

  const [phase1Open, setPhase1Open] = useState(false);
  const [phase2Open, setPhase2Open] = useState(false);
  const [phase3Open, setPhase3Open] = useState(true);

  useEffect(() => {
    void loadResumes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const resumeIdFromQuery = searchParams.get('resumeId');
    if (resumeIdFromQuery && resumes.find((r) => r.id === resumeIdFromQuery)) {
      void selectResume(resumeIdFromQuery);
    } else if (!selectedResumeId && resumes.length > 0) {
      const resumeWithJobs =
        resumes.find((r) => (r['filtered-jobs-count'] || 0) > 0) ?? resumes[0];
      void selectResume(resumeWithJobs.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumes, searchParams]);

  useEffect(() => {
    const step = searchParams.get('step');
    if (step === '3') {
      setPhase3Open(true);
    }
  }, [searchParams]);

  const loadResumes = async () => {
    setLoadingResumes(true);
    setError('');

    try {
      const resumeList = await resumeService.getResumes();
      setResumes(resumeList || []);
    } catch (err: any) {
      console.error('Error loading resumes for Jobs For You:', err);
      setError('Failed to load resumes: ' + err.message);
    } finally {
      setLoadingResumes(false);
    }
  };

  const selectResume = async (resumeId: string) => {
    if (resumeId === selectedResumeId) return;
    setSelectedResumeId(resumeId);
    await loadJobs(resumeId);
  };

  const loadJobs = async (resumeId: string): Promise<JobForYou[]> => {
    setLoadingJobs(true);
    setError('');
    setAiMessage('');
    setMatchMessage('');

    try {
      const jobResults = await resumeService.getJobsForResume(resumeId);
      const list = jobResults || [];
      setJobs(list);
      return list;
    } catch (err: any) {
      console.error('Error loading jobs for resume:', err);
      setError(err.message || 'Failed to load jobs for this resume.');
      return [];
    } finally {
      setLoadingJobs(false);
    }
  };

  const currentResume = resumes.find((r) => r.id === selectedResumeId) || null;
  const currentResumeId = currentResume?.id ?? null;
  const currentResumeAiCount = currentResume?.['ai-title-match-count'] || 0;
  const currentResumeLastProcessed = currentResume?.['most-recent-processing'] ?? null;
  const currentResumeFilteredCount = currentResume?.['filtered-jobs-count'] || 0;
  const hasAiMatches = useMemo(
    () => jobs.some((job) => job.aiTitleMatch !== null && job.aiTitleMatch !== undefined),
    [jobs],
  );

  const step1Complete = Boolean(currentResume && (currentResume['filtered-jobs-count'] || 0) > 0);
  const step2Complete = Boolean(currentResume && (currentResume['ai-title-match-count'] || 0) > 0);
  const step3Complete = useMemo(() => jobs.some((j) => j.jobFit != null), [jobs]);

  // Default to AI matches list when this resume already has AI title matches; only re-apply when
  // resume or its AI count changes so "← All jobs" stays until then.
  useEffect(() => {
    if (!currentResumeId) return;
    setViewStep(currentResumeAiCount > 0 ? 'ai' : 'main');
  }, [currentResumeId, currentResumeAiCount]);

  useEffect(() => {
    setCsvExportScopeUserSet(false);
    setStage3SearchQuery('');
  }, [selectedResumeId]);

  useEffect(() => {
    if (!currentResumeId) return;
    const hasPriorScan =
      Boolean(currentResumeLastProcessed) || currentResumeFilteredCount > 0;
    setProcessJobWindow(hasPriorScan ? 'new' : 'all');
  }, [currentResumeId, currentResumeLastProcessed, currentResumeFilteredCount]);

  useEffect(() => {
    if (csvExportScopeUserSet) return;
    setCsvExportScope(step3Complete ? 'fit_and_network' : 'all');
  }, [step3Complete, csvExportScopeUserSet]);

  const platformsWithCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const job of jobs) {
      const platform = job.platform || 'Unknown';
      counts[platform] = (counts[platform] || 0) + 1;
    }
    return counts;
  }, [jobs]);

  const companiesWithCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const job of jobs) {
      if (selectedPlatform !== 'all' && (job.platform || 'Unknown') !== selectedPlatform) {
        continue;
      }
      const company = job.sourceCompany || 'Unknown company';
      counts[company] = (counts[company] || 0) + 1;
    }
    return counts;
  }, [jobs, selectedPlatform]);

  const jobsPassingRecencyOnly = useMemo(
    () =>
      jobs.filter((job) =>
        jobPassesDateFilter(job, datePreset, customDateFrom, customDateTo),
      ),
    [jobs, datePreset, customDateFrom, customDateTo],
  );

  const filteredJobsForDisplay = useMemo(() => {
    return jobs.filter((job) => {
      const platform = job.platform || 'Unknown';

      if (viewMode === 'platform') {
        if (selectedCompany && platform !== selectedCompany) {
          return false;
        }
      } else if (selectedPlatform !== 'all' && platform !== selectedPlatform) {
        return false;
      }

      if (viewMode === 'company' && selectedCompany) {
        const company = job.sourceCompany || 'Unknown company';
        if (company !== selectedCompany) return false;
      }

      if (viewStep === 'ai' && job.aiTitleMatch !== true) return false;

      if (
        !jobPassesDateFilter(job, datePreset, customDateFrom, customDateTo)
      ) {
        return false;
      }

      return true;
    });
  }, [
    jobs,
    selectedPlatform,
    viewMode,
    selectedCompany,
    viewStep,
    datePreset,
    customDateFrom,
    customDateTo,
  ]);

  const jobsForCsvExport = useMemo(() => {
    const pool = jobsPassingRecencyOnly;
    if (csvExportScope === 'all') return pool;
    if (csvExportScope === 'ai_title') {
      return pool.filter((job) => job.aiTitleMatch === true);
    }
    return pool.filter((job) => jobHasDescriptionFit(job) && jobHasCompanyNetworkScore(job));
  }, [jobs, jobsPassingRecencyOnly, csvExportScope]);

  const jobsWithFit = useMemo(() => {
    const filtered = jobs.filter(
      (job) =>
        job.jobFit !== null &&
        job.jobFit !== undefined &&
        jobPassesDateFilter(job, datePreset, customDateFrom, customDateTo),
    );
    return filtered.sort((a, b) => {
      const scoreA =
        calculateConfidenceScore(
          a.connectionScore,
          a.jobFit,
          networkWeight,
          fitWeight,
          a.connectionStrength,
        ) ?? 0;
      const scoreB =
        calculateConfidenceScore(
          b.connectionScore,
          b.jobFit,
          networkWeight,
          fitWeight,
          b.connectionStrength,
        ) ?? 0;
      if (scoreB !== scoreA) return scoreB - scoreA;
      return fitTieBreakRank(b.jobFit) - fitTieBreakRank(a.jobFit);
    });
  }, [jobs, networkWeight, fitWeight, datePreset, customDateFrom, customDateTo]);

  const stage3VisibleJobs = useMemo(() => {
    return jobsWithFit.filter((job) => jobMatchesStage3Search(job, stage3SearchQuery));
  }, [jobsWithFit, stage3SearchQuery]);

  const jobFitCounts = useMemo(() => {
    const counts = { high: 0, medium: 0, low: 0 };
    for (const job of stage3VisibleJobs) {
      if (job.jobFit === 'high') counts.high++;
      else if (job.jobFit === 'medium') counts.medium++;
      else if (job.jobFit === 'low') counts.low++;
    }
    return counts;
  }, [stage3VisibleJobs]);

  const hasAiTrueMatches = useMemo(() => {
    if (!currentResume) return false;
    return (currentResume['ai-title-match-count'] || 0) > 0;
  }, [currentResume]);

  const fullPipelineBusy = processingJobs || aiMatching || jobFitMatching;
  const queryCount = Array.isArray(currentResume?.queries) ? currentResume?.queries.length ?? 0 : 0;

  const runFullPipeline = async () => {
    if (!currentResume) return;
    setError('');
    setProcessMessage('');
    setAiMessage('');
    setMatchMessage('');

    setProcessingJobs(true);
    setProcessScanProgress(null);
    let incrementalCutoff: string | null = null;
    let incrementalNewlyAdded = 0;
    try {
      const result = await resumeService.processJobsForResume(
        currentResume.id,
        allCompanies,
        (e) => setProcessScanProgress(e),
        processJobWindow,
      );
      const isIncremental = result.scanMode === 'incremental' || processJobWindow !== 'all';
      const added = typeof result.newlyAdded === 'number' ? result.newlyAdded : result.processedCount;
      incrementalCutoff = result.cutoff ?? null;
      incrementalNewlyAdded = added;

      if (result.processedCount === 0 && result.consideredCount === 0) {
        if (result.message && result.message.includes('connections')) {
          setError(result.message);
          return;
        }
        if (isIncremental) {
          setProcessMessage(
            'No new jobs found. Existing AI title matches and fit results are still shown.',
          );
          await loadResumes();
          const jobsAfterProcess = await loadJobs(currentResume.id);
          if (jobsAfterProcess.length === 0) {
            setError(
              'No jobs in your list yet. Run the pipeline once with “Full rescan” to build your initial list.',
            );
            return;
          }
          if (added === 0) {
            return;
          }
        } else {
          setError(
            `No jobs found. ${result.message || 'Check connections and job database.'}`,
          );
          return;
        }
      } else {
        if (isIncremental) {
          setProcessMessage(
            `Stage 1: scanned ${result.consideredCount.toLocaleString()} postings and saved ${added} new jobs.`,
          );
        } else {
          setProcessMessage(
            `Stage 1: scanned ${result.consideredCount.toLocaleString()} postings and saved ${result.processedCount} jobs.`,
          );
        }
        await loadResumes();
        const jobsAfterProcess = await loadJobs(currentResume.id);
        if (jobsAfterProcess.length === 0) {
          setError(
            'No jobs are in your list after scanning. Add jobs to the database or adjust options, then run the pipeline again.',
          );
          return;
        }
        if (isIncremental && added === 0) {
          setProcessMessage('No new jobs found. Existing AI title matches and fit results are still shown.');
          return;
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to process jobs.');
      return;
    } finally {
      setProcessingJobs(false);
      setProcessScanProgress(null);
    }

    setAiMatching(true);
    setAiTitleProgress(null);
    try {
      const result = await resumeService.runAiTitleMatching(
        currentResume.id,
        northAmericaOnly,
        incrementalCutoff,
        (e) => setAiTitleProgress(e),
      );
      const aiProcessed = typeof result.processedJobs === 'number' ? result.processedJobs : result.total;
      setAiMessage(
        incrementalCutoff
          ? `Stage 2: checked ${aiProcessed} new jobs and matched ${result.matchedCount}.`
          : `Stage 2: matched ${result.matchedCount} of ${result.total} jobs.`,
      );
      await loadResumes();
      await loadJobs(currentResume.id);
      setViewStep('ai');
      setViewMode('list');
      if (incrementalCutoff && result.matchedCount === 0) {
        setProcessMessage('');
        setMatchMessage('Stage 3 skipped: none of the new jobs passed AI title match.');
        return;
      }
    } catch (err: any) {
      console.error('Error running AI title matching:', err);
      setError(err.message || 'Failed to run AI title matching.');
      return;
    } finally {
      setAiMatching(false);
      setAiTitleProgress(null);
    }

    setJobFitMatching(true);
    setMatchFitProgress(null);
    try {
      const result = await resumeService.matchJobsWithDescriptions(
        currentResume.id,
        networkWeight,
        fitWeight,
        searchGoogleJobs,
        searchAmazonJobs,
        matchRecentDomainFocus,
        incrementalCutoff,
        (e) => setMatchFitProgress(e),
      );

      setMatchMessage(
        incrementalCutoff
          ? `Stage 3: scored ${result.processedNewCount ?? 0} new jobs. Stage 1 added ${incrementalNewlyAdded} new jobs.`
          : `Stage 3: scored ${result.matchedCount} jobs${result.skippedCount > 0 ? `, skipped ${result.skippedCount}` : ''}.`,
      );
      await loadResumes();
      setProcessMessage('');
      setAiMessage('');
      setPhase1Open(false);
      setPhase2Open(false);
      setPhase3Open(true);
    } catch (err: any) {
      console.error('Error matching jobs:', err);
      setError(err.message || 'Failed to match jobs.');
    } finally {
      setJobFitMatching(false);
      setMatchFitProgress(null);
    }
  };

  const scrollToStep = (n: 1 | 2 | 3) => {
    if (n === 1) setPhase1Open(true);
    else if (n === 2) setPhase2Open(true);
    else setPhase3Open(true);
    const ref = n === 1 ? step1Ref : n === 2 ? step2Ref : step3Ref;
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const next = new URLSearchParams(searchParams);
    next.set('step', String(n));
    setSearchParams(next, { replace: true });
  };

  const handleExportCsv = () => {
    if (!currentResume || jobsForCsvExport.length === 0) return;
    const stamp = new Date().toISOString().slice(0, 10);
    const rec = 'dated';
    const scopeSlug =
      csvExportScope === 'all' ? 'all-jobs' : csvExportScope === 'ai_title' ? 'ai-title-match' : 'matched-jobs';
    downloadJobsCsv(
      jobsForCsvExport,
      `jobs-csv-${scopeSlug}-${rec}-${currentResume.id}-${stamp}`,
      networkWeight,
      fitWeight,
    );
  };

  return (
    <div className="min-h-screen bg-ts-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Recommended jobs</h1>
              <p className="text-gray-600">
                Configure options below, then use <strong className="font-medium">Run</strong> to scan
                jobs, run AI title match, and score description fit in one go.
              </p>
            </div>
            <div className="flex gap-3">
              {isAdmin && (
                <button
                  onClick={() => navigate('/job-finder')}
                  className="px-4 py-2 text-sm font-medium text-ts-gold hover:text-ts-gold-deep hover:bg-ts-cream rounded-lg transition-colors"
                >
                  Fetch Additional Jobs
                </button>
              )}
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Dashboard
              </button>
            </div>
          </div>

          {/* Stepper — admin only (replaced by results toggle for all users) */}
          {isAdmin && (
            <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
              <p className="text-xs font-medium text-gray-500 mb-3">Pipeline</p>
              <div className="flex flex-wrap items-center gap-2 md:gap-4">
                {[
                  { n: 1 as const, label: 'Save matching jobs', done: step1Complete },
                  { n: 2 as const, label: 'AI title match', done: step2Complete },
                  { n: 3 as const, label: 'Matches', done: step3Complete },
                ].map((s, i) => (
                  <React.Fragment key={s.n}>
                    {i > 0 && <span className="text-gray-300 hidden sm:inline">→</span>}
                    <button
                      type="button"
                      onClick={() => scrollToStep(s.n)}
                      className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium border transition-colors ${
                        s.done
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                          : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <span
                        className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                          s.done ? 'bg-emerald-600 text-white' : 'bg-gray-300 text-gray-700'
                        }`}
                      >
                        {s.done ? '✓' : s.n}
                      </span>
                      {s.label}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {currentResume && (
            <div className="bg-white rounded-xl shadow border border-gray-100 p-3">
              <div className="flex flex-col lg:flex-row lg:items-start lg:gap-5">

                {/* Left column: main controls */}
                <div className="flex-1 space-y-2">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900">Job Recommendation Options</h2>
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                      TopSeeds scans the job database and saves postings that match your resume titles and then scores description fit.
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    {isAdmin && (
                      <label
                        htmlFor="process-job-window"
                        className="block text-sm font-medium text-gray-800"
                      >
                        Phase 1 — Job ingest timing
                      </label>
                    )}
                    <select
                      id="process-job-window"
                      value={processJobWindow}
                      onChange={(e) => setProcessJobWindow(e.target.value as ProcessJobWindow)}
                      disabled={fullPipelineBusy}
                      className="w-full max-w-md rounded-lg border border-gray-300 bg-white text-gray-900 text-sm px-3 py-2 shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-60"
                    >
                      <option value="all">Full rescan (replaces your entire filtered list)</option>
                      <option value="new">New only — jobs added to database since your last run</option>
                      <option value="15d">Last 15 days — jobs with createdAt within 15 days of last run</option>
                      <option value="30d">Last 30 days — jobs with createdAt within 30 days of last run</option>
                      <option value="60d">Last 60 days — jobs with createdAt within 60 days of last run</option>
                    </select>
                    {isAdmin && (
                      <p className="text-xs text-gray-500 leading-relaxed">
                        Uses each posting&apos;s <span className="font-medium">createdAt</span> (when it entered our database).
                        Non-full modes only <span className="font-medium">add genuinely new jobs</span> to your list —
                        existing jobs and their AI scores are never touched. First-time runs use a full scan automatically.
                      </p>
                    )}
                  </div>

                  {/* Advanced options — mobile accordion only (desktop shows right column) */}
                  <div className="lg:hidden rounded-lg border border-gray-200">
                    <button
                      type="button"
                      onClick={() => setAdvancedOptionsOpen((open) => !open)}
                      className="flex w-full items-center justify-between px-4 py-3 text-left"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">Advanced options</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {allCompanies ? 'All companies' : 'Connection companies only'} •{' '}
                          {northAmericaOnly ? 'North America only' : 'All regions'} •{' '}
                          {searchGoogleJobs ? 'Google jobs on' : 'Google jobs off'} •{' '}
                          {searchAmazonJobs ? 'Amazon jobs on' : 'Amazon jobs off'} •{' '}
                          {matchRecentDomainFocus ? 'Recent-domain focus on' : 'Recent-domain focus off'}
                        </p>
                      </div>
                      <span className="text-sm text-gray-500">
                        {advancedOptionsOpen ? '▾' : '▸'}
                      </span>
                    </button>
                    <div className={advancedOptionsOpen ? 'border-t border-gray-200 p-4' : 'hidden'}>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {isAdmin && (
                          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={allCompanies}
                              onChange={(e) => setAllCompanies(e.target.checked)}
                              className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span>
                              <span className="font-medium">Phase 1 — Include all companies</span>
                              <span className="block text-xs text-gray-500 mt-0.5">
                                When off, only companies with connection data are considered.
                              </span>
                            </span>
                          </label>
                        )}
                        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={northAmericaOnly}
                            onChange={(e) => setNorthAmericaOnly(e.target.checked)}
                            className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span>
                            <span className="font-medium">Phase 2 — North American jobs only</span>
                            <span className="block text-xs text-gray-500 mt-0.5">
                              Other locations are skipped for AI title matching.
                            </span>
                          </span>
                        </label>
                        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={searchGoogleJobs}
                            onChange={(e) => setSearchGoogleJobs(e.target.checked)}
                            className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span>
                            <span className="font-medium">Add Google careers jobs</span>
                            <span className="block text-xs text-gray-500 mt-0.5">
                              Before fit scoring, Gemini finds up to five Google postings and includes them in the run.
                            </span>
                          </span>
                        </label>
                        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={searchAmazonJobs}
                            onChange={(e) => setSearchAmazonJobs(e.target.checked)}
                            className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span>
                            <span className="font-medium">Add Amazon careers jobs</span>
                            <span className="block text-xs text-gray-500 mt-0.5">
                              Before fit scoring, Gemini finds up to five Amazon postings and includes them in the run.
                            </span>
                          </span>
                        </label>
                        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={matchRecentDomainFocus}
                            onChange={(e) => setMatchRecentDomainFocus(e.target.checked)}
                            className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span>
                            <span className="font-medium">Focus fit on recent work domains</span>
                            <span className="block text-xs text-gray-500 mt-0.5">
                              Emphasizes industries from your most recent roles when scoring fit.
                            </span>
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <button
                      type="button"
                      onClick={() => void runFullPipeline()}
                      disabled={fullPipelineBusy || loadingJobs}
                      className={`px-5 py-2.5 text-sm font-semibold rounded-lg text-white ${
                        fullPipelineBusy || loadingJobs
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-ts-navy hover:bg-ts-charcoal'
                      }`}
                    >
                      {fullPipelineBusy
                        ? processingJobs
                          ? 'Phase 1 of 3 — Scanning jobs…'
                          : aiMatching
                            ? 'Phase 2 of 3 — AI title matching…'
                            : jobFitMatching
                              ? 'Matches…'
                              : 'Working…'
                        : 'Run'}
                    </button>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => navigate(`/job-finder?resumeId=${currentResume.id}`)}
                        className="text-sm text-ts-gold hover:underline text-left"
                      >
                        Open Fetch Additional Jobs (Adzuna)
                      </button>
                    )}
                  </div>

                  <div className="rounded-md border border-indigo-200/70 bg-indigo-50/60 px-3 py-2 text-xs text-indigo-900">
                    Ready: <span className="font-semibold">{queryCount}</span> queries,{' '}
                    <span className="font-semibold">{processJobWindow === 'all' ? 'full rescan' : processJobWindow}</span>,{' '}
                    <span className="font-semibold">{allCompanies ? 'all companies' : 'connection companies only'}</span>,{' '}
                    <span className="font-semibold">{northAmericaOnly ? 'North America only' : 'all regions'}</span>.
                  </div>

                  {/* Unified pipeline status bar — visible only while pipeline is running */}
                  {(processingJobs || aiMatching || jobFitMatching) && (
                    <div className="rounded-lg border border-ts-border bg-ts-cream/90 px-4 py-3 text-sm text-gray-800 transition-all duration-300">
                      {processingJobs && (
                        <p className="font-medium text-gray-900">
                          {processScanProgress
                            ? processScanProgress.phase === 'saving'
                              ? `Running pipeline — Phase 1 of 3 · Saving ${processScanProgress.matchedSoFar.toLocaleString()} jobs…`
                              : `Running pipeline — Phase 1 of 3 · Scanned ${processScanProgress.consideredCount.toLocaleString()} postings · ${
                                  processScanProgress.scanMode === 'incremental'
                                    ? `${processScanProgress.matchedSoFar.toLocaleString()} new jobs saved`
                                    : `${processScanProgress.matchedSoFar.toLocaleString()} matches saved`
                                }`
                            : 'Running pipeline — Phase 1 of 3 · Starting scan…'}
                        </p>
                      )}
                      {aiMatching && (
                        <>
                          <div className="flex flex-wrap justify-between gap-2 mb-2">
                            <p className="font-medium text-gray-900">
                              {aiTitleProgress
                                ? `Running pipeline — Phase 2 of 3 · AI title filter · ${aiTitleProgress.processedJobs} of ${aiTitleProgress.totalJobs} jobs`
                                : 'Running pipeline — Phase 2 of 3 · AI title filter · Starting…'}
                            </p>
                            {aiTitleProgress?.etaSecondsApprox != null && (
                              <span className="text-xs text-gray-500">
                                {formatEtaSeconds(aiTitleProgress.etaSecondsApprox)} remaining
                              </span>
                            )}
                          </div>
                          {aiTitleProgress && aiTitleProgress.totalJobs > 0 && (
                            <div className="h-2 rounded-full bg-blue-200/80 overflow-hidden">
                              <div
                                className="h-full bg-ts-navy transition-[width] duration-300"
                                style={{ width: `${Math.min(100, (100 * aiTitleProgress.processedJobs) / aiTitleProgress.totalJobs)}%` }}
                              />
                            </div>
                          )}
                        </>
                      )}
                      {jobFitMatching && (
                        <>
                          <div className="flex flex-wrap justify-between gap-2 mb-2">
                            <p className="font-medium text-gray-900">
                              {matchFitProgress
                                ? `Running pipeline — Matches · ${matchFitProgress.processedJobs} of ${matchFitProgress.totalJobs} descriptions${matchFitProgress.scoredSoFar > 0 ? ` · ${matchFitProgress.scoredSoFar} scored` : ''}`
                                : 'Running pipeline — Matches · Preparing…'}
                            </p>
                            {matchFitProgress?.etaSecondsApprox != null && (
                              <span className="text-xs text-gray-500">
                                {formatEtaSeconds(matchFitProgress.etaSecondsApprox)} remaining
                              </span>
                            )}
                          </div>
                          {matchFitProgress && matchFitProgress.totalJobs > 0 && (
                            <div className="h-2.5 rounded-full bg-blue-200/80 overflow-hidden">
                              <div
                                className="h-full bg-ts-navy transition-[width] duration-300 ease-out"
                                style={{ width: `${Math.min(100, (100 * matchFitProgress.processedJobs) / matchFitProgress.totalJobs)}%` }}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Right column: advanced options (desktop only) */}
                <div className="hidden lg:block lg:w-56 shrink-0 pt-0.5">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Advanced options</p>
                  <div className="space-y-3">
                    {isAdmin && (
                      <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={allCompanies}
                          onChange={(e) => setAllCompanies(e.target.checked)}
                          className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span>
                          <span className="font-medium">Include all companies</span>
                          <span className="block text-xs text-gray-500 mt-0.5">
                            When off, only companies with connection data are considered.
                          </span>
                        </span>
                      </label>
                    )}
                    <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={northAmericaOnly}
                        onChange={(e) => setNorthAmericaOnly(e.target.checked)}
                        className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span>
                        <span className="font-medium">North American jobs only</span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                          Other locations are skipped for AI title matching.
                        </span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={searchGoogleJobs}
                        onChange={(e) => setSearchGoogleJobs(e.target.checked)}
                        className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span>
                        <span className="font-medium">Add Google careers jobs</span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                          Gemini finds up to five Google postings and includes them in the run.
                        </span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={searchAmazonJobs}
                        onChange={(e) => setSearchAmazonJobs(e.target.checked)}
                        className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span>
                        <span className="font-medium">Add Amazon careers jobs</span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                          Gemini finds up to five Amazon postings and includes them in the run.
                        </span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={matchRecentDomainFocus}
                        onChange={(e) => setMatchRecentDomainFocus(e.target.checked)}
                        className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span>
                        <span className="font-medium">Focus fit on recent domains</span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                          Emphasizes industries from your most recent roles when scoring fit.
                        </span>
                      </span>
                    </label>
                  </div>
                </div>

              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {aiMessage && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800 whitespace-pre-line">
              {aiMessage}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6 border border-ts-border">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Your resumes</h2>
                {loadingResumes && <span className="text-xs text-gray-500">Refreshing…</span>}
              </div>

              {resumes.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-gray-600 mb-3 text-sm">No resumes found.</p>
                  <button
                    onClick={() => navigate('/')}
                    className="px-4 py-2 bg-ts-navy text-white text-sm font-medium rounded-lg hover:bg-ts-charcoal transition-colors"
                  >
                    Go to Dashboard
                  </button>
                </div>
              ) : (
                <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
                  {resumes.map((resume) => {
                    const jobCount = resume['filtered-jobs-count'] || 0;
                    const isSelected = resume.id === selectedResumeId;

                    return (
                      <button
                        key={resume.id}
                        type="button"
                        onClick={() => void selectResume(resume.id)}
                        className={`w-full text-left border rounded-lg px-4 py-3 text-sm transition-all ${
                          isSelected
                            ? 'border-ts-gold bg-ts-cream shadow-sm'
                            : 'border-gray-200 bg-white hover:border-ts-gold/40 hover:bg-ts-cream'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="mr-3">
                            <p className="font-medium text-gray-900 truncate">
                              {resume.sourceFilename || 'Unnamed Resume'}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {resume.contact?.location || resume.contact?.email || 'No contact info'}
                            </p>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="text-xs font-medium text-gray-700">{jobCount} jobs</span>
                            <span className="text-[10px] text-gray-400">
                              {resume['most-recent-processing'] ? 'Processed' : 'Not processed yet'}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Pipeline stats — shown below the selected resume when it has been processed */}
              {currentResume && (
                <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-xs space-y-2">
                  <p className="font-semibold text-gray-700 mb-1">Last match run</p>

                  {(processMessage || matchMessage) && (
                    <p className="text-gray-600 italic">
                      {processMessage || matchMessage}
                    </p>
                  )}

                  {/* Phase 1 */}
                  <div>
                    <p className="text-gray-500 font-medium">Job scan</p>
                    <p className="text-gray-700">
                      {formatLastProcessed(currentResume['most-recent-processing'])}
                    </p>
                    <p className="text-gray-600">
                      {currentResume['filtered-jobs-count'] ?? 0} jobs saved
                      {typeof currentResume['last-job-scan-count'] === 'number'
                        ? ` · ${currentResume['last-job-scan-count'].toLocaleString()} scanned`
                        : ''}
                    </p>
                  </div>

                  {/* Phase 2 */}
                  {(currentResume['ai-title-match-count'] ?? 0) > 0 && (
                    <div>
                      <p className="text-gray-500 font-medium">AI job title filter</p>
                      <p className="text-gray-600">
                        {currentResume['ai-title-match-count']} matched
                      </p>
                    </div>
                  )}

                  {/* Phase 3 */}
                  {jobs.some(j => j.jobFit != null) && (
                    <div>
                      <p className="text-gray-500 font-medium">AI job description matching</p>
                      <p className="text-gray-600">
                        {jobs.filter(j => j.jobFit === 'high').length} high
                        {' · '}{jobs.filter(j => j.jobFit === 'medium').length} medium
                        {' · '}{jobs.filter(j => j.jobFit === 'low').length} low
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="lg:col-span-2 space-y-6">
              {!currentResume ? (
                <div className="bg-white rounded-xl shadow-lg p-12 text-center text-gray-600 text-sm border border-ts-border">
                  {loadingResumes ? (
                    <>
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ts-gold mx-auto mb-3" />
                      Loading resumes…
                    </>
                  ) : (
                    'Select a resume to run the pipeline.'
                  )}
                </div>
              ) : (
                <>
                  {/* Shared recency + CSV */}
                  <div className="bg-white rounded-xl shadow-lg border border-gray-100 px-4 py-3">
                    <div className="flex flex-wrap items-end gap-3">
                      <button
                        type="button"
                        onClick={handleExportCsv}
                        disabled={jobs.length === 0 || jobsForCsvExport.length === 0}
                        className="px-3 py-1.5 text-xs font-medium rounded-md bg-ts-navy text-white hover:bg-ts-charcoal disabled:opacity-40 whitespace-nowrap self-end"
                      >
                        Export Jobs to CSV
                      </button>

                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">Date Range</label>
                        <select
                          value={datePreset}
                          onChange={(e) => setDatePreset(e.target.value as JobDatePreset)}
                          className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white"
                        >
                          <option value="all">Any time</option>
                          <option value="7d">Last 7 days</option>
                          <option value="30d">Last 30 days</option>
                          <option value="90d">Last 90 days</option>
                          <option value="custom">Custom…</option>
                        </select>
                      </div>

                      {datePreset === 'custom' && (
                        <>
                          <div>
                            <label className="block text-[10px] text-gray-500 mb-0.5">From</label>
                            <input
                              type="date"
                              value={customDateFrom}
                              onChange={(e) => setCustomDateFrom(e.target.value)}
                              className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-gray-500 mb-0.5">To</label>
                            <input
                              type="date"
                              value={customDateTo}
                              onChange={(e) => setCustomDateTo(e.target.value)}
                              className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white"
                            />
                          </div>
                        </>
                      )}

                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">CSV rows</label>
                        <select
                          value={csvExportScope}
                          onChange={(e) => {
                            setCsvExportScopeUserSet(true);
                            setCsvExportScope(e.target.value as CsvExportScope);
                          }}
                          className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white"
                        >
                          <option value="all">All jobs in list</option>
                          <option value="ai_title">AI title matched only</option>
                          <option value="fit_and_network">Matched jobs</option>
                        </select>
                      </div>


                      <span className="text-[10px] text-gray-500 whitespace-nowrap self-end pb-1">
                        {jobs.length === 0
                          ? 'No jobs loaded'
                          : `${jobsForCsvExport.length} row${jobsForCsvExport.length === 1 ? '' : 's'}`}
                      </span>
                    </div>
                  </div>

                  {/* Step 1 — admin only */}
                  {isAdmin && (
                  <section
                    ref={step1Ref}
                    id="pipeline-step-1"
                    className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-l-blue-500"
                  >
                    <button
                      type="button"
                      onClick={() => setPhase1Open((o) => !o)}
                      aria-expanded={phase1Open}
                      className="w-full flex items-start justify-between gap-3 text-left rounded-lg -mx-1 -mt-1 px-1 py-1 hover:bg-gray-50 transition-colors"
                    >
                      <h2 className="text-lg font-semibold text-gray-900">
                        Phase 1 — Save matching jobs
                      </h2>
                      <span className="text-gray-400 text-lg shrink-0 leading-none pt-0.5" aria-hidden>
                        {phase1Open ? '▲' : '▼'}
                      </span>
                    </button>
                    <div className={phase1Open ? '' : 'hidden'}>
                        <p className="text-xs text-gray-500 mb-4 mt-1">
                          Results from the database scan (run via <strong className="font-medium">Run</strong>{' '}
                          above).
                        </p>
                        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 space-y-1">
                          <p>
                            <span className="font-medium text-gray-900">Last processed:</span>{' '}
                            {formatLastProcessed(currentResume['most-recent-processing'])}
                          </p>
                          <p>
                            <span className="font-medium text-gray-900">Filtered jobs saved:</span>{' '}
                            {currentResume['filtered-jobs-count'] ?? 0}
                          </p>
                          <p>
                            <span className="font-medium text-gray-900">Jobs scanned last run:</span>{' '}
                            {typeof currentResume['last-job-scan-count'] === 'number'
                              ? currentResume['last-job-scan-count']
                              : '—'}
                          </p>
                        </div>
                    </div>
                  </section>
                  )}

                  {/* Step 2 — AI title matches browser */}
                  <section
                    ref={step2Ref}
                    id="pipeline-step-2"
                    className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-l-indigo-500"
                  >
                    <button
                      type="button"
                      onClick={() => setPhase2Open((o) => !o)}
                      aria-expanded={phase2Open}
                      className="w-full flex items-start justify-between gap-3 text-left rounded-lg -mx-1 -mt-1 px-1 py-1 hover:bg-gray-50 transition-colors"
                    >
                      <h2 className="text-lg font-semibold text-gray-900">
                        Browse matched jobs
                      </h2>
                      <span className="text-gray-400 text-lg shrink-0 leading-none pt-0.5" aria-hidden>
                        {phase2Open ? '▲' : '▼'}
                      </span>
                    </button>
                    <div className={phase2Open ? '' : 'hidden'}>
                    <p className="text-xs text-gray-500 mb-3 mt-1">
                      AI title matching runs as part of <strong className="font-medium">Run</strong>. Use
                      the view controls below to switch between all saved jobs and AI-matched jobs.
                    </p>

                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-base font-medium text-gray-900">
                          {viewStep === 'ai'
                            ? `AI matches — ${currentResume.sourceFilename || 'resume'}`
                            : `Jobs — ${currentResume.sourceFilename || 'resume'}`}
                        </h3>
                        {jobs.length > 0 && (
                          <p className="text-[11px] text-gray-400 mt-1">
                            {viewStep === 'ai'
                              ? `Showing ${filteredJobsForDisplay.length} of ${jobs.length} (AI view)`
                              : `Showing ${filteredJobsForDisplay.length} of ${jobs.length}`}
                            {selectedPlatform !== 'all' && ` · platform: ${selectedPlatform}`}
                            {viewMode === 'company' && selectedCompany && ` · ${selectedCompany}`}
                            {typeof currentResume['filtered-jobs-count'] === 'number' &&
                              currentResume['filtered-jobs-count'] !== jobs.length && (
                                <span className="block text-[10px] text-amber-700/90 mt-0.5">
                                  Resume card shows {currentResume['filtered-jobs-count']} saved — loaded {jobs.length}{' '}
                                  (refresh after a full pipeline run if these drift).
                                </span>
                              )}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        {loadingJobs && (
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-ts-gold" />
                            Loading…
                          </div>
                        )}
                        {hasAiMatches && viewStep === 'main' && (
                          <button
                            type="button"
                            onClick={() => {
                              setViewStep('ai');
                              setViewMode('list');
                            }}
                            className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
                          >
                            View AI matches
                          </button>
                        )}
                        {viewStep === 'ai' && (
                          <button
                            type="button"
                            onClick={() => {
                              setViewStep('main');
                              setViewMode('list');
                            }}
                            className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
                          >
                            ← All jobs
                          </button>
                        )}
                      </div>
                    </div>

                    {jobs.length > 0 && (
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <div className="inline-flex bg-gray-100 rounded-lg p-1">
                          <button
                            type="button"
                            onClick={() => setViewMode('list')}
                            className={`px-3 py-1 text-xs font-medium rounded-md ${
                              viewMode === 'list'
                                ? 'bg-white text-ts-gold shadow-sm'
                                : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            List
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setViewMode('company');
                              setSelectedCompany(null);
                            }}
                            className={`px-3 py-1 text-xs font-medium rounded-md ${
                              viewMode === 'company'
                                ? 'bg-white text-ts-gold shadow-sm'
                                : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            By company
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setViewMode('platform');
                              setSelectedCompany(null);
                            }}
                            className={`px-3 py-1 text-xs font-medium rounded-md ${
                              viewMode === 'platform'
                                ? 'bg-white text-ts-gold shadow-sm'
                                : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            By platform
                          </button>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-medium text-gray-700">Platform:</span>
                          <select
                            value={selectedPlatform}
                            onChange={(e) => {
                              setSelectedPlatform(e.target.value);
                              setSelectedCompany(null);
                            }}
                            className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white"
                          >
                            <option value="all">All ({jobs.length})</option>
                            {Object.entries(platformsWithCounts).map(([platform, count]) => (
                              <option key={platform} value={platform}>
                                {platform} ({count})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {loadingJobs ? (
                      <div className="flex items-center justify-center py-10">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ts-gold mr-3" />
                        <span className="text-sm text-gray-500">Loading jobs…</span>
                      </div>
                    ) : jobs.length === 0 ? (
                      <div className="text-center py-10 text-sm text-gray-600">
                        <p className="mb-3">No saved jobs yet. Run the full pipeline above.</p>
                      </div>
                    ) : viewMode === 'list' ? (
                      <div className="space-y-4 max-h-[480px] overflow-y-auto pr-1">
                        {filteredJobsForDisplay.map((job) => (
                          <JobCard key={job.id} job={job} />
                        ))}
                      </div>
                    ) : (
                      <div className="flex gap-4 max-h-[480px]">
                        <div className="w-1/3 border-r border-gray-100 pr-3 overflow-y-auto">
                          <h3 className="text-xs font-semibold text-gray-700 mb-2">
                            {viewMode === 'company' ? 'Companies' : 'Platforms'}
                          </h3>
                          <div className="space-y-1">
                            {(viewMode === 'company'
                              ? Object.entries(companiesWithCounts)
                              : Object.entries(platformsWithCounts)
                            )
                              .sort((a, b) => b[1] - a[1])
                              .map(([group, count]) => {
                                const isSelected = selectedCompany === group;
                                return (
                                  <button
                                    key={group}
                                    type="button"
                                    onClick={() =>
                                      setSelectedCompany((prev) => (prev === group ? null : group))
                                    }
                                    className={`w-full text-left px-3 py-1.5 rounded-md text-xs flex justify-between ${
                                      isSelected ? 'bg-ts-cream text-ts-gold' : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                                  >
                                    <span className="truncate">{group}</span>
                                    <span className="text-gray-500">{count}</span>
                                  </button>
                                );
                              })}
                          </div>
                        </div>
                        <div className="flex-1 overflow-y-auto space-y-4">
                          {filteredJobsForDisplay.length === 0 ? (
                            <p className="text-xs text-gray-500">No jobs match filters.</p>
                          ) : (
                            filteredJobsForDisplay.map((job) => <JobCard key={job.id} job={job} />)
                          )}
                        </div>
                      </div>
                    )}
                    </div>
                  </section>

                  {/* Step 3 */}
                  <section
                    ref={step3Ref}
                    id="pipeline-step-3"
                    className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-l-emerald-500"
                  >
                    <button
                      type="button"
                      onClick={() => setPhase3Open((o) => !o)}
                      aria-expanded={phase3Open}
                      className="w-full flex items-start justify-between gap-3 text-left rounded-lg -mx-1 -mt-1 px-1 py-1 hover:bg-gray-50 transition-colors"
                    >
                      <h2 className="text-lg font-semibold text-gray-900">
                        Matches
                      </h2>
                      <span className="text-gray-400 text-lg shrink-0 leading-none pt-0.5" aria-hidden>
                        {phase3Open ? '▲' : '▼'}
                      </span>
                    </button>
                    {phase3Open && (
                      <>
                    {isAdmin && (
                    <p className="text-xs text-gray-500 mb-4 mt-1">
                      Fit scores come from <strong className="font-medium">Run</strong> (phase 3). Order:
                      highest overall match % first; when tied, High fit before Medium before Low.
                    </p>
                    )}

                    <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Confidence weighting
                        </span>
                        <div className="flex gap-2 text-xs">
                          <span className="px-2 py-1 bg-ts-gold/10 text-ts-gold rounded border border-ts-border">
                            Network {networkWeight}%
                          </span>
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded border border-ts-border">
                            Fit {fitWeight}%
                          </span>
                        </div>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={networkWeight}
                        onChange={(e) => setNetworkWeight(Number(e.target.value))}
                        disabled={fullPipelineBusy}
                        className="w-full h-2 rounded-lg cursor-pointer disabled:opacity-50"
                        style={{
                          background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${networkWeight}%, #a855f7 ${networkWeight}%, #a855f7 100%)`,
                        }}
                      />
                      <p className="text-[10px] text-gray-500 mt-1">
                        Affects displayed match % and the exported &quot;Overall match (%)&quot; column. Set before
                        running the pipeline; disabled while a run is in progress.
                      </p>
                    </div>

                    {!hasAiTrueMatches ? (
                      <p className="text-sm text-gray-600 mb-3">
                        Run the full pipeline above. At least one AI title match is required before fit scores appear
                        here.
                      </p>
                    ) : (
                      <>
                        <p className="text-xs text-gray-500 mb-4">
                          {jobs.filter((j) => j.aiTitleMatch === true).length} AI title matches
                          {stage3VisibleJobs.length > 0 && ` · ${stage3VisibleJobs.length} visible with fit`}
                        </p>

                        <div className="mb-3">
                          <label
                            htmlFor="stage3-search"
                            className="block text-[11px] font-medium text-gray-600 mb-1"
                          >
                            Search shown fit jobs
                          </label>
                          <input
                            id="stage3-search"
                            type="text"
                            value={stage3SearchQuery}
                            onChange={(e) => setStage3SearchQuery(e.target.value)}
                            placeholder="Filter by title, company, location, or platform"
                            className="w-full max-w-xl rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white text-gray-900"
                          />
                        </div>

                        {stage3VisibleJobs.length > 0 ? (
                          <>
                            <div className="flex flex-wrap gap-2 text-xs mb-3">
                              <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                                High {jobFitCounts.high}
                              </span>
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">
                                Medium {jobFitCounts.medium}
                              </span>
                              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full">
                                Low {jobFitCounts.low}
                              </span>
                            </div>
                            <div className="space-y-3 max-h-[420px] overflow-y-auto">
                              {stage3VisibleJobs.map((job) => (
                                <MatchedJobCard
                                  key={job.uid || job.id}
                                  job={job}
                                  networkWeight={networkWeight}
                                  fitWeight={fitWeight}
                                />
                              ))}
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-500">
                            No fit jobs match the current filters/search. Adjust filters or run the full pipeline.
                          </div>
                        )}
                      </>
                    )}
                      </>
                    )}
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface JobCardProps {
  job: JobForYou;
}

const JobCard: React.FC<JobCardProps> = ({ job }) => {
  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-ts-gold/40 hover:shadow-md transition-all">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1">{job.postingTitle || 'Untitled role'}</h3>
          <p className="text-sm text-gray-600 mb-1">{job.sourceCompany || 'Unknown company'}</p>
          <p className="text-xs text-gray-500">
            {Array.isArray(job.location)
              ? job.location.join(' • ')
              : job.location || 'Location not specified'}
          </p>
          {job.platform && <p className="mt-1 text-xs text-gray-400">Platform: {job.platform}</p>}
        </div>
        <div className="flex flex-col items-end gap-2">
          <span
            className={`text-xs font-medium px-2 py-1 rounded-full ${
              job.aiTitleMatch
                ? 'bg-green-100 text-green-800'
                : job.aiTitleMatch === false
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-500'
            }`}
          >
            {job.aiTitleMatch === null
              ? 'AI match pending'
              : job.aiTitleMatch
                ? 'AI title match'
                : 'AI no match'}
          </span>
          {job.jobFit && (
            <span
              className={`text-xs font-medium px-2 py-1 rounded-full ${
                job.jobFit === 'high'
                  ? 'bg-emerald-100 text-emerald-800'
                  : job.jobFit === 'medium'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-orange-100 text-orange-700'
              }`}
            >
              {job.jobFit === 'high' ? 'High Fit' : job.jobFit === 'medium' ? 'Medium Fit' : 'Low Fit'}
            </span>
          )}
          {job.connectionStrength && job.sourceCompany && (
            <Link
              to={`/connections?view=companies&company=${encodeURIComponent(job.sourceCompany)}`}
              className={`inline-block text-xs font-medium px-2 py-1 rounded-full hover:opacity-90 ${
                job.connectionStrength === 'Strong'
                  ? 'bg-purple-100 text-purple-800'
                  : job.connectionStrength === 'Medium'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-slate-100 text-slate-600'
              }`}
              title={`View connections at ${job.sourceCompany}`}
            >
              {job.connectionStrength} Connection
            </Link>
          )}
          {job.connectionStrength && !job.sourceCompany && (
            <span
              className={`text-xs font-medium px-2 py-1 rounded-full ${
                job.connectionStrength === 'Strong'
                  ? 'bg-purple-100 text-purple-800'
                  : job.connectionStrength === 'Medium'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-slate-100 text-slate-600'
              }`}
            >
              {job.connectionStrength} Connection
            </span>
          )}
          {job.posting && (
            <button
              type="button"
              onClick={() => window.open(job.posting, '_blank', 'noopener,noreferrer')}
              className="inline-flex items-center px-3 py-1 text-xs font-medium text-ts-gold border border-ts-border rounded-full hover:bg-ts-cream"
            >
              View posting
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

interface MatchedJobCardProps {
  job: JobForYou;
  networkWeight: number;
  fitWeight: number;
}

const MatchedJobCard: React.FC<MatchedJobCardProps> = ({ job, networkWeight, fitWeight }) => {
  const fitConfig = {
    high: {
      border: 'border-emerald-300',
      badge:
        'bg-emerald-50 text-emerald-800 border border-emerald-200',
      label: 'High Fit',
      icon: '✓',
    },
    medium: {
      border: 'border-amber-300',
      badge:
        'bg-amber-50 text-amber-900 border border-amber-200',
      label: 'Medium Fit',
      icon: '~',
    },
    low: {
      border: 'border-gray-300',
      badge:
        'bg-gray-50 text-gray-700 border border-gray-200',
      label: 'Low Fit',
      icon: '·',
    },
  };

  const fit = job.jobFit && fitConfig[job.jobFit] ? fitConfig[job.jobFit] : fitConfig.low;

  const confidenceScore = calculateConfidenceScore(
    job.connectionScore,
    job.jobFit,
    networkWeight,
    fitWeight,
    job.connectionStrength,
  );
  const confidenceConfig =
    confidenceScore !== null
      ? confidenceScore >= 70
        ? { bg: 'bg-emerald-600', text: 'text-white', ring: 'ring-emerald-200' }
        : confidenceScore >= 50
          ? { bg: 'bg-amber-500', text: 'text-white', ring: 'ring-amber-200' }
          : { bg: 'bg-gray-500', text: 'text-white', ring: 'ring-gray-200' }
      : null;

  return (
    <div className={`border-2 rounded-lg p-5 bg-white ${fit.border} hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3 mb-2">
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 text-base leading-tight mb-1">
                {job.postingTitle || 'Untitled role'}
              </h4>
              <p className="text-sm text-gray-700 font-medium">{job.sourceCompany || 'Unknown company'}</p>
            </div>
            {confidenceScore !== null && confidenceConfig && (
              <div className="flex-shrink-0">
                <div
                  className={`inline-flex items-center justify-center px-3 py-1.5 text-sm font-semibold rounded-md ${confidenceConfig.bg} ${confidenceConfig.text} ring-2 ${confidenceConfig.ring}`}
                >
                  {confidenceScore}% Match
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md ${fit.badge}`}>
              <span className="text-base leading-none">{fit.icon}</span>
              {fit.label}
            </span>

            {job.connectionStrength && job.sourceCompany ? (
              <Link
                to={`/connections?view=companies&company=${encodeURIComponent(job.sourceCompany)}`}
                className={`inline-flex items-center text-xs font-medium px-3 py-1.5 rounded-md border transition-colors ${
                  job.connectionStrength === 'Strong'
                    ? 'bg-purple-50 text-purple-800 border-purple-200 hover:bg-purple-100'
                    : job.connectionStrength === 'Medium'
                      ? 'bg-indigo-50 text-indigo-800 border-indigo-200 hover:bg-indigo-100'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                }`}
                title={`View connections at ${job.sourceCompany}`}
              >
                {job.connectionStrength} Connection
              </Link>
            ) : job.connectionStrength ? (
              <span
                className={`inline-flex items-center text-xs font-medium px-3 py-1.5 rounded-md border ${
                  job.connectionStrength === 'Strong'
                    ? 'bg-purple-50 text-purple-800 border-purple-200'
                    : job.connectionStrength === 'Medium'
                      ? 'bg-indigo-50 text-indigo-800 border-indigo-200'
                      : 'bg-slate-50 text-slate-700 border-slate-200'
                }`}
              >
                {job.connectionStrength} Connection
              </span>
            ) : null}
          </div>

          <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
            <span>
              {Array.isArray(job.location)
                ? job.location.join(' • ')
                : job.location || 'Location not specified'}
            </span>
            {job.platform && <span>{job.platform}</span>}
          </div>
        </div>

        {job.posting && (
          <button
            type="button"
            onClick={() => window.open(job.posting, '_blank', 'noopener,noreferrer')}
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-ts-gold bg-ts-cream border border-ts-border rounded-md hover:bg-ts-gold/10"
          >
            View Job
          </button>
        )}
      </div>
    </div>
  );
};

export default JobsForYouPage;




